<template>
  <div class="checkbox-group">
    <div v-for="option in options" :key="option.value" :style="style" class="checkbox-option" :class="{ disabled: disabled }" @click="!disabled && handleChange(option.value)">
      <input
        type="checkbox"
        :style="getSelectStyle"
        :id="`checkbox-${option.value}`"
        :value="option.value"
        :disabled="disabled"
        :checked="isChecked(option.value)"
        @change.stop
        class="checkbox-input" />
      <label :for="`checkbox-${option.value}`" class="checkbox-label">
        {{ option.label }}
      </label>
    </div>
    <!-- 其他选项的输入框 -->
    <div class="other-input-wrapper" v-if="showOtherInput">
      <input
        v-if="inputFormat === 1"
        :style="getOtherStyle"
        type="text"
        v-model="otherText"
        @input="handleOtherInput"
        :placeholder="getOtherPlaceholder"
        :maxlength="200"
        :disabled="disabled"
        class="other-input" />
      <text-number-input v-else :style="getOtherStyle" v-model="otherText" @change="handleOtherInput" :placeholder="getOtherPlaceholder" :wordLimit="200" :disabled="disabled" class="other-input" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import TextNumberInput from './textNumberInput.vue'
import Toast from '@/h5/components/toast'
import LGSdk from '@/packages/plugins/languageSDK/index'
import { debounce } from 'lodash'
import { computeRatioPx } from '@/packages/plugins/utils'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  options: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean
  },
  inputFormat: {
    type: Number
  },
  otherValue: String,
  style: {
    type: Object,
    default: () => {}
  }
})

const emit = defineEmits(['change', 'update:modelValue', 'update:otherValue'])

const otherText = ref(props.otherValue || null)

// 获取其他选项的配置
const otherOption = computed(() => props.options.find((option) => option.otherOption))

// 是否显示其他输入框
const showOtherInput = computed(() => {
  return otherOption.value && props.modelValue.includes(otherOption.value.value)
})

// 获取其他选项的placeholder
const getOtherPlaceholder = computed(() => otherOption.value?.attrs?.placeholder || '')

// 获取其他选项的样式
const getOtherStyle = computed(() => {
  /* 判断 */
  const otherStyle = {
    width: props.style.otherWidth,
    padding: `${props.style.paddingTop} ${props.style.paddingRight} ${props.style.paddingBottom} ${props.style.paddingLeft}`,
    border: 0,
    borderRadius: 0,
    fontSize: props.style.fontSize,
    background: props.style.otherBackground
  }
  if (props.style.otherBorderSize) {
    if (props.style.onlyBorderBottom) {
      otherStyle.borderBottom = props.style.otherBorder
    } else {
      otherStyle.borderRadius = props.style.otherBorderRadius
      otherStyle.border = props.style.otherBorder
    }
  }
  return otherStyle
})

// 获取选择框变量样式
const getSelectStyle = computed(() => {
  return {
    '--select-bg': props.style.selectBg,
    '--selected-bg': props.style.selectedBg,
    '--selected-mark': props.style.selectMark,
    '--border-color': props.style.selectBorderColor,
    '--selected-font-size': computeRatioPx(22),
    '--selected-bottom': computeRatioPx(2),
    border: `${computeRatioPx(2)} solid var(--border-color)`,
    width: computeRatioPx(28),
    height: computeRatioPx(28)
  }
})

const isChecked = (value) => {
  return props.modelValue.includes(value)
}

const handleChange = (value) => {
  let newValue = [...props.modelValue]
  if (isChecked(value)) {
    if (otherOption.value?.value === value) {
      otherText.value = ''
      emit('update:otherValue', '')
    }
    newValue = newValue.filter((v) => v !== value)
  } else {
    newValue.push(value)
  }
  emit('change', newValue)
  emit('update:modelValue', newValue)
}

const showToast = debounce(
  (customMessage) => {
    Toast && Toast({ message: customMessage, duration: 1500 })
  },
  500,
  { leading: true, trailing: false }
)

// 处理其他选项的输入
const handleOtherInput = () => {
  if (otherText.value.length >= 200) {
    showToast(LGSdk.t('survey.maxInput'))
  }
  emit('update:otherValue', otherText.value)
}
</script>

<style scoped>
.checkbox-group {
  display: flex;
  flex-direction: column;
  /* gap: 0.8571rem; */
}

.checkbox-option {
  display: flex;
  align-items: center;
  transition: all 0.3s;
  cursor: pointer;
  width: max-content;
  max-width: 100%;
  box-sizing: border-box;
}

.checkbox-option.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.checkbox-option.disabled .checkbox-input {
  cursor: not-allowed;
}

.checkbox-input {
  margin: 0.2143rem 0.5714rem 0.2143rem 0;
  /* 新增 */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  position: relative;
  cursor: pointer;
  border-radius: 0.14286rem;
  background: var(--select-bg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkbox-input:checked {
  background: var(--selected-bg);
}

.checkbox-input:checked::before {
  content: '✓';
  font-size: var(--selected-font-size);
  color: var(--selected-mark);
  position: relative;
  bottom: var(--selected-bottom);
}

.checkbox-label {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.other-input-wrapper {
  width: calc(100% - 1.7143rem);
}

.other-input {
  width: 100%;
  padding: 0.5714rem;
  border: 0.0714rem solid #ddd;
  border-radius: 0.2857rem;
  font-size: 1rem;
}
</style>
